import { Capacitor } from "@capacitor/core";
import type { CountryConfig } from "@orvis/core";
import { ADMOB_TEST_DEVICE_IDS, adsMode } from "./adsEnv.js";
import { AppOpenAd, appOpenAdAvailable } from "./appOpenAd.js";

/**
 * AdMob integration.
 *
 * The same web bundle ships to browser (via Firebase Hosting) and to
 * native iOS + Android WebViews (via Capacitor). AdMob only makes sense
 * under Capacitor — on plain browser the calls no-op.
 *
 * `@capacitor-community/admob` is loaded lazily so browser users never
 * pay for its bytes.
 *
 * Banners: on Android phones they're in-feed rows (see
 * components/InlineAd.tsx + the app-local InlineBanner native plugin), so
 * nothing is pinned over the UI. Android TV (a native view inside a D-pad
 * list is a focus trap) and iOS (no InlineBanner plugin yet) keep the
 * persistent bottom banner from `@capacitor-community/admob`.
 *
 * App Open ads: the plugin has no App Open format, so Android uses the
 * app-local AppOpenAd native plugin (see appOpenAd.ts). iOS shows none yet.
 *
 * Invalid traffic: every request is gated by `adsMode()` (adsEnv.ts) — live
 * units only on Android release builds, Google's test units in every other
 * build, nothing at all on TV / Test Lab. Rules: docs/ads-policy.md.
 */

/** No app-open ad within this long of any full-screen ad (app-open or interstitial). */
const APP_OPEN_MIN_INTERVAL_MS = 60_000;
/**
 * No interstitial within this long of any full-screen ad (interstitial or
 * app-open). Without it every channel tap was an interstitial, which AdMob
 * flags as "more ads than publisher content".
 */
const INTERSTITIAL_MIN_INTERVAL_MS = 180_000;
/** How long a cold start waits for an app-open ad before giving up on it. */
const APP_OPEN_COLD_START_WAIT_MS = 4_000;
/** On resume, only show an ad that's (nearly) ready — don't hold the user up. */
const APP_OPEN_RESUME_WAIT_MS = 1_000;

let initialized = false;
/** Request Google's test creatives instead of ours (adsMode() === "test"). */
let testing = true;
let bannerListenersRegistered = false;
let loadedInterstitialAdId: string | null = null;
let interstitialsDisabled = false;
let lastFullscreenAdAt = 0;
let fullscreenAdShowing = false;
let resolveReady: (ok: boolean) => void = () => {};
const ready = new Promise<boolean>((r) => {
  resolveReady = r;
});

/** Resolves once AdMob.initialize() settles — true if the SDK is usable. */
export function adsReady(): Promise<boolean> {
  return ready;
}

/**
 * True when banners should be in-feed rows instead of the pinned bottom
 * banner: Android phones/tablets only (see the module doc).
 */
export function inlineBannersEnabled(tv: boolean): boolean {
  return !tv && Capacitor.getPlatform() === "android";
}

/** Rows before the first in-feed ad, rows between ads, and the per-list cap. */
const INLINE_AD_FIRST = 6;
const INLINE_AD_EVERY = 12;
const INLINE_AD_MAX_PER_LIST = 6;

/**
 * Whether an in-feed ad row goes right after list row `index` (0-based) in a
 * list of `length` rows. Never after the last row — an ad shouldn't end a list.
 */
export function inlineAdAfter(index: number, length: number): boolean {
  if (index >= length - 1) return false;
  const n = index + 1 - INLINE_AD_FIRST;
  if (n < 0 || n % INLINE_AD_EVERY !== 0) return false;
  return n / INLINE_AD_EVERY < INLINE_AD_MAX_PER_LIST;
}

/** Cheap runtime check — true on iOS/Android under Capacitor, false in a plain browser. */
export function isNativeAdPlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Toggle interstitials off. Called by App.tsx when `useTvMode()` reports
 * a TV — the Play Store TV UX guidelines discourage interstitial ads that
 * interrupt a D-pad flow, and banner-only is the right revenue model on
 * Android TV. Also gates the app-open substitute (same reasoning).
 */
export function setInterstitialsEnabled(enabled: boolean): void {
  interstitialsDisabled = !enabled;
}

async function loadAdMobModule() {
  if (!isNativeAdPlatform()) return null;
  try {
    const mod = await import("@capacitor-community/admob");
    return mod;
  } catch (err) {
    console.warn("[ads] failed to load @capacitor-community/admob:", err);
    return null;
  }
}

/**
 * Called once from App boot. Safe on web (returns immediately).
 * Initializes the AdMob plugin so the first interstitial call has no
 * cold-start cost, then kicks off the banner and (outside TV mode) the
 * first interstitial preload.
 */
export async function initAds(country: CountryConfig, opts: { tv: boolean }): Promise<void> {
  if (initialized) return;
  const mode = await adsMode();
  // TV: no ads at all — D-pad focus lands on the banner (accidental clicks)
  // and a stream left running piles up impressions nobody sees.
  if (mode === "off" || opts.tv) {
    resolveReady(false);
    return;
  }
  testing = mode === "test";
  const mod = await loadAdMobModule();
  if (!mod) {
    resolveReady(false);
    return;
  }
  try {
    await mod.AdMob.initialize({
      // Registers the team's own phones so they get test creatives even on a
      // release build. Test mode itself is `isTesting` on each request below.
      initializeForTesting: true,
      testingDevices: ADMOB_TEST_DEVICE_IDS,
    });
    initialized = true;
    resolveReady(true);
    // TV never shows interstitials (setInterstitialsEnabled(false) runs
    // before this), so don't spend a request preloading one there.
    if (country.ads.interstitialUnitId && !country.ads.disableInterstitials && !interstitialsDisabled) {
      void preloadInterstitial(country.ads.interstitialUnitId);
    }
    if (country.ads.appOpenUnitId && !country.ads.disableInterstitials && !interstitialsDisabled) {
      void preloadAppOpen(country.ads.appOpenUnitId);
    }
    if (!inlineBannersEnabled(opts.tv)) void showBanner(country);
  } catch (err) {
    console.warn("[ads] initialize failed:", err);
    resolveReady(false);
  }
}

async function preloadInterstitial(adId: string): Promise<void> {
  const mod = await loadAdMobModule();
  if (!mod) return;
  try {
    await mod.AdMob.prepareInterstitial({ adId, isTesting: testing });
    loadedInterstitialAdId = adId;
  } catch (err) {
    console.warn("[ads] prepareInterstitial failed:", err);
    loadedInterstitialAdId = null;
  }
}

/**
 * Show an interstitial before letting the user through to a full-screen
 * TV player. Matches the legacy Expo behavior at
 * `legacy/frontend/<country>/app/components/TVStationCardItem.component.tsx`.
 *
 * Resolves regardless of whether an ad actually shows — the caller
 * should always proceed to playback.
 */
export async function showInterstitialForChannelTap(country: CountryConfig): Promise<void> {
  const adId = country.ads.interstitialUnitId;
  if (!adId) return;
  if (country.ads.disableInterstitials) return;
  if (interstitialsDisabled) return; // TV mode
  if (fullscreenAdShowing || Date.now() - lastFullscreenAdAt < INTERSTITIAL_MIN_INTERVAL_MS) return;
  if (!(await adsReady())) return;
  const mod = await loadAdMobModule();
  if (!mod) return;
  try {
    if (loadedInterstitialAdId !== adId) {
      await mod.AdMob.prepareInterstitial({ adId, isTesting: testing });
    }
    // Leaving for the interstitial and coming back fires appStateChange —
    // stamp it so that resume doesn't stack an app-open ad on top.
    lastFullscreenAdAt = Date.now();
    await mod.AdMob.showInterstitial();
    lastFullscreenAdAt = Date.now();
    loadedInterstitialAdId = null;
    // Warm the next one in the background so the second channel tap is
    // also instant.
    void preloadInterstitial(adId);
  } catch (err) {
    console.warn("[ads] showInterstitial failed:", err);
    loadedInterstitialAdId = null;
  }
}

async function preloadAppOpen(adId: string): Promise<boolean> {
  if (!appOpenAdAvailable()) return false;
  try {
    await AppOpenAd.load({ adId });
    return true;
  } catch (err) {
    console.warn("[ads] app-open load failed:", err);
    return false;
  }
}

/** Resolves true if `p` settles true within `ms`; the work carries on either way. */
function within(p: Promise<boolean>, ms: number): Promise<boolean> {
  return Promise.race([p, new Promise<boolean>((r) => setTimeout(() => r(false), ms))]);
}

/**
 * App Open ad on cold start and on foreground-resume. Called from
 * `useAppOpenAd`. A cold start waits briefly for the first ad to load; a
 * resume only shows one that's already (nearly) cached and otherwise just
 * warms the next. A cooldown after any full-screen ad guards against
 * appStateChange flicker (notification shade, quick app-switch, returning
 * from an interstitial) that isn't really a fresh "app open".
 */
export async function maybeShowAppOpenAd(
  country: CountryConfig,
  opts: { coldStart: boolean },
): Promise<void> {
  const adId = country.ads.appOpenUnitId;
  if (!adId) return;
  if (country.ads.disableInterstitials) return;
  if (!appOpenAdAvailable()) return;
  if (!cooledDown()) return;
  if (!(await adsReady())) return;
  if (interstitialsDisabled) return; // TV mode (set during App boot, so check after init)

  const ready = await within(
    preloadAppOpen(adId),
    opts.coldStart ? APP_OPEN_COLD_START_WAIT_MS : APP_OPEN_RESUME_WAIT_MS,
  );
  // The user may have left again, or another ad may have started, while we waited.
  if (!ready || !cooledDown() || document.visibilityState !== "visible") return;

  fullscreenAdShowing = true;
  lastFullscreenAdAt = Date.now();
  try {
    await AppOpenAd.show();
  } catch (err) {
    console.warn("[ads] app-open show failed:", err);
  } finally {
    fullscreenAdShowing = false;
    lastFullscreenAdAt = Date.now();
  }
  void preloadAppOpen(adId);
}

function cooledDown(): boolean {
  return !fullscreenAdShowing && Date.now() - lastFullscreenAdAt >= APP_OPEN_MIN_INTERVAL_MS;
}

/**
 * Shows a persistent bottom banner (TV / iOS only). Called once from `initAds`. Writes the
 * banner's rendered height to `--admob-banner-height` so the layout can
 * reserve space for it (the banner is a native overlay, not a DOM element).
 */
async function showBanner(country: CountryConfig): Promise<void> {
  const adId = country.ads.bannerUnitId;
  if (!adId) return;
  const mod = await loadAdMobModule();
  if (!mod) return;
  try {
    if (!bannerListenersRegistered) {
      bannerListenersRegistered = true;
      mod.AdMob.addListener(mod.BannerAdPluginEvents.SizeChanged, (size: { height: number }) => {
        setBannerHeightVar(size.height > 0 ? `${size.height}px` : "0px");
      });
      mod.AdMob.addListener(mod.BannerAdPluginEvents.FailedToLoad, () => {
        setBannerHeightVar("0px");
      });
    }
    await mod.AdMob.showBanner({
      adId,
      adSize: mod.BannerAdSize.ADAPTIVE_BANNER,
      isTesting: testing,
      position: mod.BannerAdPosition.BOTTOM_CENTER,
    });
  } catch (err) {
    console.warn("[ads] showBanner failed:", err);
  }
}

function setBannerHeightVar(value: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--admob-banner-height", value);
}
